import HomeColours from 'app/views/home/config/HomeColours';

import type { SystemsGridConfig } from 'app/views/home/webgl/particles/targets/ParticleGridTargets';

/**
 * The systems dot-grid set-point — THE place to tune the broken dot grid
 * behind the systems scene (uniform with systemsPhaseEntries.ts: every
 * department answers "where are the knobs" the same way). SystemsDepartment
 * hands this to ParticleGridTargets.configure() at construction; the
 * workbench (dev/phase/SystemsWorkbench) does the same, so /dev/phase/systems
 * tunes identically.
 *
 * Every value below equals SYSTEMS_GRID_DEFAULTS (the legacy constants) —
 * the grid renders pixel-identical to the reference 'particles' type until
 * someone edits a number here. See SystemsGridConfig's header in
 * ParticleGridTargets.ts for which fields need a grid rebuild vs apply
 * per-tick (configure() handles the invalidation either way).
 */
export const systemsGridConfig: SystemsGridConfig = {
	//-- Grid geometry (rebuilds the grid) -------------------------------------
	//Cell size in px at the reference viewport width; the on-screen cell is
	//round(this * scale) where scale = innerWidth / the reference width below,
	//floored at the min-scale for the layout:
	baseBlockSizePx: 30,
	landscapeMinGridScale: 0.5,
	portraitMinGridScale: 0.15,
	landscapeScaleReferenceWidthPx: 1920,
	portraitScaleReferenceWidthPx: 1300,
	//Portrait grid: full innerWidth wide, this fraction of innerHeight tall:
	portraitGridHeightFactor: 0.5,
	//Landscape grid width as a fraction of innerWidth (narrow = viewports at
	//or under HomeZonesConfig.noOverlapSize) and height = innerHeight - inset:
	landscapeGridWidthFactorNarrow: 0.9,
	landscapeGridWidthFactorWide: 0.8,
	landscapeGridHeightInsetPx: 175,

	//-- The "broken" look (rebuilds the grid) ---------------------------------
	//Chance a cell is a dead pixel (no dot, no spawn target). Higher = sparser
	//grid. The edge seed applies in the border band (cols/rows each side) —
	//larger than the interior seed, so the grid frays at its edges:
	deadPixelSeedLandscape: 0.5,
	deadPixelSeedPortrait: 0.55,
	deadPixelSeedEdgeLandscape: 0.75,
	edgeBandCols: 5,
	edgeBandRows: 4,
	//Live dots: size roll (ceil(random * this) px -> 1px or 2px dots) and the
	//palette they pick from:
	pointSizeMax: 1.99,
	pointColours: HomeColours.greys,

	//-- Safe zone + tracking (apply per-tick) ---------------------------------
	//Portrait grids centre on this fraction of innerHeight (landscape centres
	//on the tracked systems-grid item):
	portraitCenterYFactor: 0.35,
	//The item-clearing hole at the grid centre, as fractions of the tracked
	//sprite's width/height, nudged right by the inset:
	safeZoneWidthFactor: 0.95,
	safeZoneHeightFactor: 0.52,
	safeZoneInsetXPx: 10,

	//-- Guide visibility over drag progress (apply per-tick) ------------------
	//The guide fades from min- to max-alpha as drag progress crosses the fade
	//start on the way into the systems zone:
	guideFadeStartProgress: 0.7,
	guideMaxAlpha: 0.85,
	guideMaxAlphaMobile: 0.48,
	guideMinAlpha: 0.55,
	guideMinAlphaMobile: 0.35,

	//-- Guide rendering (redraws the guide Graphics) --------------------------
	//Base alpha of the dot Graphics (multiplies the fade above) and the dot
	//shrink under Config.mobileBreakpoint:
	guideAlpha: 0.6,
	guideDotScaleMobile: 0.7,
	//Safe-zone cells still draw a dim dot this often, in this colour:
	safeZoneDotChance: 0.4,
	safeZoneDotColour: '#666666',
	//The sparse dot bands drawn above/below the grid proper (mobile's bottom
	//band is a fraction of innerHeight instead of a fixed px height):
	sparsePaddingTopPx: 100,
	sparsePaddingBottomPx: 100,
	sparsePaddingBottomMobileHeightFactor: 0.4,
	//Sparse-band dots: size roll, per-cell chance, and colour (this often from
	//the palette, else the base colour):
	extraDotSizeMax: 1.99,
	extraDotDensity: 0.2,
	extraDotColourChance: 0.75,
	extraDotColours: HomeColours.blues,
	extraDotBaseColour: '#ffffff'
};
